import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Crown, Calendar, Check, X, ShieldCheck, AlertTriangle, TrendingUp } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { openRazorpayCheckout } from "../lib/razorpay";
import { useAuth } from "../context/AuthContext";

function daysUntil(iso) {
  if (!iso) return null;
  try {
    const ms = new Date(iso).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / 86400000));
  } catch { return null; }
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" });
  } catch { return iso; }
}

const PLAN_LABELS = {
  mentor_basic: "Mentor Basic",
  mentor_professional: "Mentor Professional",
};

export function MentorSubscriptionPanel() {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showCancel, setShowCancel] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [meRes, plansRes] = await Promise.allSettled([
        api.get("/mentor/subscription/me"),
        api.get("/mentor/subscription/plans"),
      ]);
      if (meRes.status === "fulfilled") {
        setState(meRes.value.data);
      } else {
        console.error("mentor/subscription/me failed:", meRes.reason?.response?.status, meRes.reason?.response?.data);
        setState({ plan: null, status: "inactive", bookable: true, hidden: false, verified: false, auto_renew: false });
        toast.error(formatApiError(meRes.reason));
      }
      if (plansRes.status === "fulfilled") {
        setPlans(plansRes.value.data.plans || []);
      } else {
        console.error("mentor/subscription/plans failed:", plansRes.reason?.response?.status);
        setPlans([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const subscribe = async (planId) => {
    setBusy(true);
    try {
      const { data: order } = await api.post("/mentor/subscription/create-order", { plan_id: planId });
      await openRazorpayCheckout({
        keyId: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "Rate-ED",
        description: order.plan_name,
        orderId: order.order_id,
        prefill: { email: user?.email, name: user?.name },
        onSuccess: async (rzp) => {
          try {
            await api.post("/mentor/subscription/verify", rzp);
            toast.success(`${PLAN_LABELS[planId]} activated!`);
            await load();
          } catch (err) {
            toast.error(formatApiError(err));
          } finally { setBusy(false); }
        },
        onDismiss: () => setBusy(false),
      });
    } catch (err) {
      toast.error(formatApiError(err));
      setBusy(false);
    }
  };

  const cancelAutoRenew = async () => {
    setBusy(true);
    try {
      await api.post("/mentor/subscription/cancel");
      toast.success("Auto-renew turned off. Access stays until expiry.");
      setShowCancel(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const resumeAutoRenew = async () => {
    setBusy(true);
    try {
      await api.post("/mentor/subscription/resume");
      toast.success("Auto-renew turned back on.");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-3xl p-6 border border-black/5" data-testid="mentor-subscription-loading">
        <p className="text-sm text-[#13A699]/70">Loading subscription…</p>
      </div>
    );
  }

  const status = state?.status;
  const plan = state?.plan;
  const isPro = plan === "mentor_professional" && status === "active";
  const isActive = status === "active";
  const isTrial = status === "trial";
  const isOverdue = status === "overdue";
  const autoRenew = state?.auto_renew !== false;
  const cancelled = isActive && !autoRenew;
  const days = daysUntil(isActive ? state?.expires_at : state?.trial_ends_at);

  const basic = plans.find((p) => p.id === "mentor_basic");
  const professional = plans.find((p) => p.id === "mentor_professional");

  return (
    <div className="space-y-4" data-testid="mentor-subscription-panel">
      {/* Status card */}
      <div
        className="rounded-3xl p-6 border-2"
        style={{
          borderColor: isOverdue ? "#DC2626" : (isPro ? "#FFD708" : "#13A69940"),
          background: isPro
            ? "linear-gradient(135deg, #FFF7ED 0%, #FFF3C4 100%)"
            : (isOverdue ? "#FEF2F2" : "#FFFFFF"),
        }}
        data-testid="mentor-subscription-status-card"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-2">
              {isPro ? (
                <Crown className="h-6 w-6" style={{ color: "#FFD708" }} fill="#FFD708" />
              ) : isOverdue ? (
                <AlertTriangle className="h-6 w-6 text-red-600" />
              ) : (
                <Crown className="h-6 w-6" style={{ color: "#13A699" }} />
              )}
              <h3
                className="font-display text-xl font-bold uppercase"
                style={{ color: "#13A699" }}
                data-testid="mentor-subscription-title"
              >
                {plan ? PLAN_LABELS[plan] : "Subscription"}
              </h3>
              {state?.verified && (
                <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-800" data-testid="mentor-verified-badge">
                  <ShieldCheck className="h-3.5 w-3.5" /> Verified
                </span>
              )}
            </div>
            <p className="text-sm" style={{ color: "#13A699" }}>
              {isTrial && (
                <span data-testid="mentor-trial-status">
                  <Calendar className="inline h-4 w-4 mr-1" />
                  {state?.trial_ends_at ? (
                    <>
                      Free trial · ends <b>{formatDate(state?.trial_ends_at)}</b>
                      {days !== null && <span className="opacity-70"> ({days} day{days === 1 ? "" : "s"} left)</span>}
                    </>
                  ) : (
                    <>Free trial · billing not yet activated by admin</>
                  )}
                </span>
              )}
              {isActive && (
                <span data-testid="mentor-active-status">
                  <Calendar className="inline h-4 w-4 mr-1" />
                  {cancelled ? "Access ends" : "Renews"} on <b>{formatDate(state?.expires_at)}</b>
                  {days !== null && <span className="opacity-70"> ({days} day{days === 1 ? "" : "s"} left)</span>}
                </span>
              )}
              {isOverdue && (
                <span className="text-red-700 font-semibold" data-testid="mentor-overdue-status">
                  Your free trial has ended. Subscribe now to keep accepting new bookings — your profile is hidden until you pay.
                </span>
              )}
            </p>
            {cancelled && (
              <div className="text-xs mt-1 font-semibold text-red-600" data-testid="mentor-cancelled-notice">
                Auto-renew is OFF &mdash; you won&apos;t be charged again.
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="mt-5 flex items-center gap-3 flex-wrap">
          {isActive && plan === "mentor_basic" && !cancelled && professional && (
            <button
              type="button"
              onClick={() => subscribe("mentor_professional")}
              disabled={busy}
              className="btn-yellow text-sm px-5 py-2.5 inline-flex items-center gap-2 disabled:opacity-60"
              data-testid="mentor-upgrade-professional-btn"
            >
              <TrendingUp className="h-4 w-4" />
              Upgrade to Professional · ₹999/mo
            </button>
          )}
          {isActive && !cancelled && (
            <button
              type="button"
              onClick={() => setShowCancel(true)}
              disabled={busy}
              className="text-sm px-5 py-2.5 rounded-full border border-red-300 text-red-600 hover:bg-red-50 inline-flex items-center gap-2 disabled:opacity-60"
              data-testid="mentor-cancel-btn"
            >
              <X className="h-4 w-4" /> Cancel Auto-renew
            </button>
          )}
          {cancelled && (
            <button
              type="button"
              onClick={resumeAutoRenew}
              disabled={busy}
              className="btn-yellow text-sm px-5 py-2.5 inline-flex items-center gap-2 disabled:opacity-60"
              data-testid="mentor-resume-btn"
            >
              <Crown className="h-4 w-4" /> Turn auto-renew back on
            </button>
          )}
        </div>
      </div>

      {/* Plan comparison — always shown so mentors can see benefits & upgrade paths */}
      {(!isActive || plan === "mentor_basic") && (
        <div className="grid md:grid-cols-2 gap-4" data-testid="mentor-plans-grid">
          {basic && (
            <PlanCard
              plan={basic}
              trialDays={90}
              highlight={false}
              currentPlan={plan}
              buttonLabel={
                (isTrial || isOverdue) ? "Subscribe · ₹499/mo" : (isActive && plan === "mentor_basic" ? "Current plan" : "Subscribe · ₹499/mo")
              }
              disabled={busy || (isActive && plan === "mentor_basic")}
              onSubscribe={() => subscribe("mentor_basic")}
              testid="mentor-plan-basic"
            />
          )}
          {professional && (
            <PlanCard
              plan={professional}
              trialDays={0}
              highlight={true}
              currentPlan={plan}
              buttonLabel="Subscribe · ₹999/mo"
              disabled={busy}
              onSubscribe={() => subscribe("mentor_professional")}
              testid="mentor-plan-professional"
            />
          )}
        </div>
      )}

      {/* Cancel confirmation modal */}
      {showCancel && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="mentor-cancel-modal">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <h4 className="font-display text-xl font-bold uppercase mb-3" style={{ color: "#13A699" }}>
              Turn off auto-renew?
            </h4>
            <p className="text-sm text-black/70">
              Your subscription stays active until <b>{formatDate(state?.expires_at)}</b>. After that, if you don&apos;t re-subscribe, your public profile will be hidden and new bookings will be blocked.
              Existing confirmed bookings will complete normally.
            </p>
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowCancel(false)}
                disabled={busy}
                className="text-sm px-4 py-2 rounded-full border border-black/20"
                data-testid="mentor-cancel-modal-back"
              >
                Keep subscription
              </button>
              <button
                type="button"
                onClick={cancelAutoRenew}
                disabled={busy}
                className="text-sm px-4 py-2 rounded-full bg-red-600 text-white disabled:opacity-60"
                data-testid="mentor-cancel-modal-confirm"
              >
                {busy ? "Cancelling…" : "Yes, cancel auto-renew"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PlanCard({ plan, trialDays, highlight, currentPlan, buttonLabel, disabled, onSubscribe, testid }) {
  const isCurrent = currentPlan === plan.id;
  return (
    <div
      className={`rounded-2xl p-5 border-2 ${highlight ? "border-[#FFD708] bg-[#FFF7ED]" : "border-black/10 bg-white"}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-2">
        {highlight ? (
          <Crown className="h-4 w-4" style={{ color: "#FFD708" }} fill="#FFD708" />
        ) : (
          <Crown className="h-4 w-4" style={{ color: "#13A699" }} />
        )}
        <h5 className="font-display text-lg font-bold uppercase" style={{ color: "#13A699" }}>{plan.name}</h5>
        {isCurrent && (
          <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-green-100 text-green-800">Current</span>
        )}
      </div>
      <div className="mt-1 text-2xl font-bold" style={{ color: "#13A699" }}>
        ₹{plan.price_inr}
        <span className="text-sm opacity-70">/mo</span>
      </div>
      {trialDays > 0 && (
        <div className="text-[11px] mt-0.5 font-semibold uppercase tracking-wide text-green-700">
          Free for first {trialDays / 30} months from launch
        </div>
      )}
      <ul className="mt-3 text-xs space-y-1" style={{ color: "#13A699" }}>
        {(plan.features || []).map((f, i) => (
          <li key={i} className="flex items-start gap-1.5"><Check className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {f}</li>
        ))}
      </ul>
      <div className="text-[11px] mt-3 opacity-70" style={{ color: "#13A699" }}>
        20% commission on every booking · Verified badge requires admin approval
      </div>
      <button
        type="button"
        onClick={onSubscribe}
        disabled={disabled}
        className={`mt-4 w-full text-sm px-4 py-2.5 rounded-full font-bold disabled:opacity-60 ${highlight ? "bg-[#13A699] text-white" : "border border-[#13A699] text-[#13A699]"}`}
        data-testid={`${testid}-subscribe`}
      >
        {buttonLabel}
      </button>
    </div>
  );
}
